import { OrderProduct, OrderStatusCode } from '@gooduncles/gu-app-schema';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-enterprise';
import { ColDef, ColGroupDef } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { FC, useMemo, useState } from 'react';

import { formatNumberForAgCell } from 'src/utils/ag-grid-util';
import { formatNumber } from 'src/utils/common';
import { formatDate } from 'src/utils/date-util';

import useOrderListForUser from 'src/hooks/swr/useOrderListForUser';

interface UserOrderProductListProps {
  userId: string;
}

type OrderProductForRow = OrderProduct & {
  orderId: string;
  orderDate: string;
  deliveryTaskFinishedAt: string | null;
};

const defaultColDef: ColDef<OrderProductForRow> = {
  sortable: true,
  resizable: true,
  filter: true,
};

/**
 * 동일한 상품의 그룹행 설정
 * {@link https://www.ag-grid.com/react-data-grid/row-selection/#example-group-selection}
 */
const autoGroupColumnDef = {
  headerName: '상품명',
  field: 'productId',
  width: 300,
  cellRenderer: 'agGroupCellRenderer',
  valueGetter: (params: any) => {
    const { fullName } = (params?.data as OrderProductForRow) ?? {};
    return fullName;
  },
};

const UserOrderProductList: FC<UserOrderProductListProps> = ({ userId }) => {
  const [, setGridRef] = useState<AgGridReact<OrderProductForRow> | null>(null);
  const { orderList } = useOrderListForUser(userId);
  const rowData: OrderProductForRow[] = useMemo(
    () =>
      orderList
        ? orderList
            .filter((order) => order.orderStatus === OrderStatusCode.DELIVERED)
            ?.flatMap((order) =>
              order.products.map((p) => ({
                orderId: order._id,
                orderDate: order.orderDate,
                deliveryTaskFinishedAt: order.deliveryTaskFinishedAt,
                ...p,
              }))
            )
        : [],
    [orderList]
  );

  const columnDefs: (ColDef<OrderProductForRow> | ColGroupDef<OrderProductForRow>)[] = useMemo(
    () => [
      {
        headerName: '상품코드',
        field: 'productId',
        width: 120,
        valueFormatter: (params: any) => {
          const value = params.value;
          const isGroup = params.node.group;
          return isGroup ? params.node?.childrenAfterGroup?.[0].data?.productId : value;
        },
      },
      { headerName: '상품명', field: 'fullName', rowGroup: true, hide: true },
      {
        headerName: '가격',
        field: 'snapshotPrice',
        aggFunc: 'avg',
        width: 120,
        valueGetter: (params) => {
          const { price, snapshotPrice } = (params?.data as OrderProductForRow) ?? {};
          return snapshotPrice ?? price ?? 0;
        },
        cellRenderer: (params: any) => formatNumber(params.value, 0),
      },
      {
        headerName: '주문 후 가격 변동',
        field: 'priceDiff',
        aggFunc: 'avg',
        width: 180,
        valueGetter: (params) => {
          const { priceDiff } = (params?.data as OrderProductForRow) ?? {};
          return priceDiff ?? 0;
        },
        cellRenderer: (params: any) => formatNumber(params.value, 0),
      },
      {
        headerName: '구매수량',
        field: 'volume',
        aggFunc: 'sum',
        valueFormatter: formatNumberForAgCell,
      },
      {
        headerName: '총액',
        field: 'totalAmount',
        maxWidth: 160,
        aggFunc: 'sum',
        sort: 'desc',
        valueGetter: (params) => {
          const { price, snapshotPrice, volume } = (params.data as OrderProductForRow) ?? {};
          return (snapshotPrice ?? price ?? 0) * (volume ?? 0);
        },
        valueFormatter: formatNumberForAgCell,
      },
      {
        headerName: '주문일',
        field: 'orderDate',
        width: 180,
        hide: true,
        valueFormatter: ({ value }) => (value ? formatDate(value, 'yyyy-MM-dd HH:mm') : ''),
      },
      {
        headerName: '배송일',
        field: 'deliveryTaskFinishedAt',
        width: 180,
        valueFormatter: ({ value }) => (value ? formatDate(value, 'yyyy-MM-dd HH:mm') : ''),
      },
    ],
    []
  );

  return (
    <div className='ag-theme-alpine' style={{ height: '100%' }}>
      <AgGridReact
        ref={setGridRef}
        rowData={rowData}
        getRowId={(params) => params.data.orderId + params.data.productId}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        autoGroupColumnDef={autoGroupColumnDef}
      />
    </div>
  );
};

export default UserOrderProductList;
